import React from "react";
import { Box, Button, Chip, Container, Paper, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

const features = [
  { icon: LanguageRoundedIcon, title: "Página editable", text: "Cambiá textos, secciones e identidad sin depender de otra persona." },
  { icon: Inventory2OutlinedIcon, title: "Productos y servicios", text: "Usá la misma plataforma para vender artículos, turnos o trabajos." },
  { icon: PaymentsOutlinedIcon, title: "Cobros y pedidos", text: "Centralizá consultas, ventas y configuraciones de pago." },
  { icon: InsightsOutlinedIcon, title: "Seguimiento real", text: "Mirá el estado de tu proyecto y encontrá rápidamente lo pendiente." },
];

export default function ResponsiveShowcase({
  onStartClick,
  primaryLabel = "Crear mi proyecto ahora",
}) {
  return (
    <Box id="herramientas" component="section" sx={{ py: { xs: 8, md: 12 }, bgcolor: "#F6F8FA", scrollMarginTop: 90 }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={3}>
          <Box sx={{ maxWidth: 720 }}>
            <Chip label="Todo conectado" sx={{ bgcolor: "#19242F", color: "#fff", fontWeight: 900 }} />
            <Typography sx={{ mt: 2, color: "#19242F", fontWeight: 950, letterSpacing: "-.045em", lineHeight: 1.04, fontSize: { xs: "2.15rem", md: "3.5rem" } }}>
              Una página que te invita a hacer, no solo a mirar.
            </Typography>
          </Box>
          <Typography sx={{ maxWidth: 420, color: "rgba(25,36,47,.64)", lineHeight: 1.7 }}>
            La interfaz prioriza el próximo paso, reduce opciones innecesarias y mantiene visibles las tareas importantes.
          </Typography>
        </Stack>

        <Box sx={{ mt: 6, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.15fr .85fr" }, gap: 2.5 }}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 5, bgcolor: "#19242F", color: "#fff", minHeight: 500, overflow: "hidden", position: "relative" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ color: "#FF9F86", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".1em", fontSize: ".76rem" }}>Panel guiado</Typography>
                <Typography sx={{ mt: .7, fontWeight: 950, fontSize: { xs: "1.8rem", md: "2.35rem" }, letterSpacing: "-.035em" }}>Tu proyecto, siempre en movimiento</Typography>
              </Box>
              <NotificationsActiveOutlinedIcon sx={{ color: "#F35A3A", fontSize: 34 }} />
            </Stack>

            <Paper elevation={0} sx={{ mt: 4, p: 2.2, borderRadius: 4, bgcolor: "#fff", color: "#19242F" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>Mi negocio</Typography>
                  <Typography sx={{ color: "rgba(25,36,47,.55)", fontSize: ".85rem" }}>Tu página está al 60%</Typography>
                </Box>
                <Chip label="Continuar" size="small" sx={{ bgcolor: "rgba(243,90,58,.11)", color: "#C83E21", fontWeight: 900 }} />
              </Stack>
              <Box sx={{ mt: 2, height: 8, borderRadius: 99, bgcolor: "rgba(25,36,47,.08)", overflow: "hidden" }}><Box sx={{ width: "60%", height: "100%", bgcolor: "#F35A3A", borderRadius: 99 }} /></Box>
              <Stack spacing={1.2} sx={{ mt: 2.5 }}>
                {[
                  [CheckCircleRoundedIcon, "Cuenta y email confirmados", true],
                  [EditRoundedIcon, "Completá los datos de tu negocio", false],
                  [Inventory2OutlinedIcon, "Cargá tu primer producto o servicio", false],
                ].map(([Icon, label, done]) => (
                  <Stack key={label} direction="row" alignItems="center" spacing={1.2} sx={{ p: 1.2, borderRadius: 2.5, bgcolor: done ? "rgba(42,157,103,.07)" : "rgba(25,36,47,.035)" }}>
                    <Icon sx={{ color: done ? "#2A9D67" : "#F35A3A", fontSize: 21 }} />
                    <Typography sx={{ fontWeight: 800, fontSize: ".9rem", flexGrow: 1 }}>{label}</Typography>
                    {!done && <ArrowForwardRoundedIcon sx={{ fontSize: 18, color: "rgba(25,36,47,.4)" }} />}
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Paper>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "1fr" }, gap: 2.5 }}>
            {features.map(({ icon: Icon, title, text }, index) => (
              <Paper key={title} elevation={0} sx={{ p: 2.5, borderRadius: 4, border: "1px solid rgba(25,36,47,.08)", bgcolor: index === 0 ? "#FFF4EF" : "#fff", display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: index === 0 ? "#F35A3A" : "rgba(25,36,47,.07)", color: index === 0 ? "#fff" : "#19242F" }}><Icon /></Box>
                <Box>
                  <Typography sx={{ color: "#19242F", fontWeight: 900, fontSize: "1.05rem" }}>{title}</Typography>
                  <Typography sx={{ mt: .6, color: "rgba(25,36,47,.62)", lineHeight: 1.55, fontSize: ".9rem" }}>{text}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        <Box sx={{ mt: 5, textAlign: "center" }}>
          <Button onClick={onStartClick} endIcon={<ArrowForwardRoundedIcon />} sx={{ color: "#F35A3A", fontWeight: 900, textTransform: "none", fontSize: "1rem" }}>
            {primaryLabel}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
